/**
 * Format gate for listing (cottage) image uploads.
 *
 * jpeg/png/webp go to S3 as-is (the API signs by file extension and the /img
 * resizer handles all three). HEIC is converted to JPEG in the browser first:
 * neither the resizer nor most desktop browsers can decode it.
 */
import { renameToJpg } from 'logic/imageResize';

export const LISTING_IMAGE_ACCEPT = '.jpeg,.jpg,.png,.webp,.heic,.heif';
// Min dimensions and max size kept at the dashboard's previous limits;
// owners-app (image-constraints.ts) is stricter: 1200x800, 6MB.
export const LISTING_IMAGE_MIN_WIDTH = 500;
export const LISTING_IMAGE_MIN_HEIGHT = 500;
export const LISTING_IMAGE_MAX_BYTES = 10 * 1024 * 1024;

// Keep in sync with the API upload validator (api/src/validators/images.js).
const DIRECT_EXT = /\.(jpe?g|png|webp)$/i;
const HEIC_EXT = /\.(heic|heif)$/i;
const HEIC_MIME = /^image\/(heic|heif)$/i;
const HEIC_JPEG_QUALITY = 0.9;

export const isHeic = (file) =>
  Boolean(file) &&
  ((typeof file.type === 'string' && HEIC_MIME.test(file.type)) ||
    (typeof file.name === 'string' && HEIC_EXT.test(file.name)));

export const isAllowedListingImage = (file) =>
  Boolean(file) && ((typeof file.name === 'string' && DIRECT_EXT.test(file.name)) || isHeic(file));

// libheif is memory hungry; a multi-select of iPhone photos must not decode
// them all at once, so conversions run one at a time.
let conversionQueue = Promise.resolve();

const convert = async (file) => {
  // ~1.3MB of libheif, so only load it when a HEIC file is actually picked.
  const { default: heic2any } = await import('heic2any');
  const result = await heic2any({ blob: file, toType: 'image/jpeg', quality: HEIC_JPEG_QUALITY });
  const blob = Array.isArray(result) ? result[0] : result;

  return new File([blob], renameToJpg(file.name), { type: 'image/jpeg' });
};

export const meetsMinDimensions = (width, height) =>
  width >= LISTING_IMAGE_MIN_WIDTH && height >= LISTING_IMAGE_MIN_HEIGHT;

export const convertHeicToJpeg = (file) => {
  const run = conversionQueue.then(() => convert(file));
  conversionQueue = run.catch(() => {});
  return run;
};
